import { SideBarOperationsProps } from "@/pages/inbox/components/sidebar/types";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApprovalsFilters,
  INBOX_INITIAL_FILTERS,
  TRACKING_INITIAL_FILTERS,
} from "@/pages/inbox/types";
import { useClickOutside } from "@/hooks";
import { ApprovalsTab, DEFAULT_DOMAIN_ID } from "@/constants";
import { useApprovalsList } from "@/pages/inbox/hooks/use-approvals-list";
import { InboxItem } from "@/services";
import { find } from "lodash";

export const useSideBarOperations = ({
  tab,
  searchQuery,
  suggests,
  searchContainerRef,
  onSetApprovalSuggests,
  onToggleFilterMode,
  onSetActiveInboxItem,
}: SideBarOperationsProps) => {
  const [_, setSearchParams] = useSearchParams();
  const [isSearchFocus, setIsSearchFocus] = useState(false);

  const [
    isShowFullSearchList,
    // setIsShowFullSearchList
  ] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);

  const initialFilters = useMemo(
    () =>
      tab === ApprovalsTab.Tracking
        ? TRACKING_INITIAL_FILTERS
        : INBOX_INITIAL_FILTERS,
    [tab],
  );

  const [filters, setFilters] =
    useState<Partial<ApprovalsFilters>>(initialFilters);

  useClickOutside(searchContainerRef, () => {
    setIsSearchFocus(false);
  });

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters, tab]);

  const isTracking = tab === ApprovalsTab.Tracking;

  const {
    isLoading,
    data,
    error,
    handleLoadMoreData,
    hasMoreData,
    onRemoveItem,
    onResetData,
    totalCount,
  } = useApprovalsList({
    tab,
    searchQuery,
    isShowFullSearchList,
    isTracking,
    appliedFilters: appliedFilters.join(";"),
  });

  const handleToggleSearchFieldFocus = (status: boolean) => {
    setIsSearchFocus(status);
  };

  const handleFocusSearchSuggests = useCallback(() => {
    handleToggleSearchFieldFocus(true);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    onSetApprovalSuggests(suggests ?? null);
    handleToggleSearchFieldFocus(false);

    // TODO uncomment when it will be stable
    // onResetData();
    // setIsShowFullSearchList(true);
  }, [onSetApprovalSuggests, suggests]);

  const getSelectedFiltersCount = useCallback(() => {
    return Object.values(filters).filter(Boolean).length;
  }, [filters]);

  const handleSetFilters = useCallback((filterName: string, value: boolean) => {
    setFilters(prevState => ({
      ...prevState,
      [filterName]: !value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const handleApplyFilters = useCallback(() => {
    onToggleFilterMode(true);

    if (getSelectedFiltersCount() > 0) {
      setAppliedFilters(
        Object.keys(filters).filter(
          key => filters[key as keyof typeof filters],
        ),
      );
    } else {
      setAppliedFilters([]);
    }

    setSearchParams("");
  }, [filters, getSelectedFiltersCount, onToggleFilterMode, setSearchParams]);

  const handleUnApplyFilters = useCallback(() => {
    setAppliedFilters([]);
    setFilters(initialFilters);
    onToggleFilterMode(false);
  }, [initialFilters, onToggleFilterMode]);

  const handleSelectRequest = useCallback(
    (item: InboxItem) => {
      onSetActiveInboxItem(item);
      setSearchParams({
        ReferenceNumber: item.ReferenceNumber,
        domainId: DEFAULT_DOMAIN_ID,
      });
    },
    [onSetActiveInboxItem, setSearchParams],
  );

  const getSelectedRequestData = useCallback(
    (referenceNumber: string) => {
      const selectedItem = find(
        data,
        request => request.ReferenceNumber === referenceNumber,
      );

      if (selectedItem) {
        onSetActiveInboxItem(selectedItem);
      }

      return selectedItem;
    },
    [data, onSetActiveInboxItem],
  );

  return {
    dataIsLoading: isLoading,
    data,
    error,
    totalCount,
    isSearchFocus,
    isShowFullSearchList,
    filters,
    appliedFilters,
    hasMoreData,
    isTracking,
    onLoadMoreData: handleLoadMoreData,
    onToggleSearchFieldFocus: handleToggleSearchFieldFocus,
    onFocusSearchSuggests: handleFocusSearchSuggests,
    onSelectRequest: handleSelectRequest,
    onSearchSubmit: handleSearchSubmit,
    onResetData,
    onSetFilters: handleSetFilters,
    onResetFilters: handleResetFilters,
    onApplyFilters: handleApplyFilters,
    onUnApplyFilters: handleUnApplyFilters,
    onGetSelectedFiltersCount: getSelectedFiltersCount,
    onGetSelectedRequestData: getSelectedRequestData,
    onRemoveRequestFromList: onRemoveItem,
  };
};
